import removeUndefined from '#shared/util/removeUndefined.js';
import isObject from '#shared/util/isObject.js';
import appDebug from '#shared/util/appDebug.js';
import isEmptyObject from '#shared/util/isEmptyObject.js';

import singleDataLayer from '#frontend/singleDataLayer/index.js';
import dtm from '#frontend/dtm/index.js';

import accountTooltipDisplay from '#frontend/dataLayer/accountTooltipDisplay.js';
import addToCart from '#frontend/dataLayer/addToCart.js';
import buyingForACompany from '#frontend/dataLayer/buyingForACompany.js';
import cartOpen from '#frontend/dataLayer/cartOpen.js';
import cartRequest from '#frontend/dataLayer/cartRequest.js';
import checkout from '#frontend/dataLayer/checkout.js';
import crossPopClick from '#frontend/dataLayer/crossPopClick.js';
import crossPopImpression from '#frontend/dataLayer/crossPopImpression.js';
import editBilling from '#frontend/dataLayer/editBilling.js';
import editCompany from '#frontend/dataLayer/editCompany.js';
import editPayment from '#frontend/dataLayer/editPayment.js';
import editShipping from '#frontend/dataLayer/editShipping.js';
import emailOpen from '#frontend/dataLayer/emailOpen.js';
import emailOpenClick from '#frontend/dataLayer/emailOpenClick.js';
import emailTypo from '#frontend/dataLayer/emailTypo.js';
import emailTypoClick from '#frontend/dataLayer/emailTypoClick.js';
import errorPageStatus from '#frontend/dataLayer/errorPageStatus.js';
import freeTrialEnd from '#frontend/dataLayer/freeTrialEnd.js';
import jsError from '#frontend/dataLayer/jsError.js';
import paymentAuthorisationFailed from '#frontend/dataLayer/paymentAuthorisationFailed.js';
import paymentSelected from '#frontend/dataLayer/paymentSelected.js';
import payPalButtonClick from '#frontend/dataLayer/payPalButtonClick.js';
import productActivation from '#frontend/dataLayer/productActivation.js';
import purchase from '#frontend/dataLayer/purchase.js';
import removeFromCart from '#frontend/dataLayer/removeFromCart.js';
import restoreCart from '#frontend/dataLayer/restoreCart.js';
import review from '#frontend/dataLayer/review.js';
import submitOrder from '#frontend/dataLayer/submitOrder.js';
import supportButton from '#frontend/dataLayer/supportButton.js';
import userData from '#frontend/dataLayer/userData.js';
import virtualPageview from '#frontend/dataLayer/virtualPageview.js';

import CookieStorage from './cookieStorage.js';

const cookieStorage = new CookieStorage();
const sharedCookieName = 'bi-cookiesList';

/**
 * Get shared cookie value
 * @param {string} sharedCookieName Name of the shared cookie
 * @returns {object} json object
 */
const getTrackingCookieValue = (sharedCookieName) => JSON.parse(cookieStorage.getItem(sharedCookieName) || '{}');

/**
 * Check if requested cookie is set in shared cookie
 * @param {string} sharedCookieName Shared (global) cookie name
 * @param {object} cookie Object with name and value
 * @returns {boolean}
 */
const isTrackingCookieSet = (sharedCookieName, cookie = {}) => {
	const cookiesObject = getTrackingCookieValue(sharedCookieName);
	return cookiesObject[cookie.name] && cookiesObject[cookie.name] === cookie.value;
};

/**
 * Set value to shared cookie
 * @param {string} sharedCookieName Shared cookie name
 * @param {object} cookie Object with cookie name and value
 */
const setTrackingCookie = (sharedCookieName, cookie) => {
	const { name, value } = cookie;
	const cookieObject = getTrackingCookieValue(sharedCookieName);
	// Set cookie name as key
	cookieObject[name] = value;
	// Set cookie to cookie storage
	cookieStorage.setItem(sharedCookieName, JSON.stringify(cookieObject), {
		path: '/'
	});
};

/**
 * @class Tracking
 */
export default class Tracking {

	/**
	 * Initialization
	 */
	constructor() {
		this.dtm = [];
		if (isObject(window)) {
			window.dataLayer = window.dataLayer || [];
			this.dataLayer = window.dataLayer;
			window.sdl = window.sdl || [];
			this.sdl = window.sdl;
			window.nortonAnalytics = window.nortonAnalytics || {};
			// If spa is set, the push to adobe is done manually, otherwise adobe loads incomplete data.
			window.nortonAnalytics.spa = true;
		}
	}

	/**
	 * Generic event push
	 * @param {object} dataLayerObject
	 * @param {object} [callOnceCookie] Indicates that the event should be called only once (unique event)
	 * @param {string} [callOnceCookie.name] The name of the cookie to be set to indicate that the event was already called
	 * @param {string|number} [callOnceCookie.value] The value of the cookie to be compared to determine whether the event should be called again
	 * @returns {object}
	 */
	_pushToDataLayer(dataLayerObject, callOnceCookie) {
		if (!isObject(callOnceCookie) || !isTrackingCookieSet(sharedCookieName, callOnceCookie)) {
			const sanitizedObject = removeUndefined(dataLayerObject);
			this.dataLayer.push(sanitizedObject);
			appDebug(`Tracking ${dataLayerObject.event}`, {
				code: 'APP_FRONTEND_UTIL',
				origin: 'tracking._pushToDataLayer',
				detail: {
					dataLayerObject
				}
			});
			// Set tracking cookie
			if (isObject(callOnceCookie)) {
				setTrackingCookie(sharedCookieName, callOnceCookie);
			}
		}
		return this;
	}

	/**
	 * Generic event push
	 * @param {object} adobeObject
	 * @param {object} [callOnceCookie] Indicates that the event should be called only once (unique event)
	 * @param {string} [callOnceCookie.name] The name of the cookie to be set to indicate that the event was already called
	 * @param {(string | number)} [callOnceCookie.value] The value of the cookie to be compared to determine whether the event should be called again
	 * @returns {object}
	 */
	_pushToAdobe(adobeObject, callOnceCookie) {
		if (!isObject(callOnceCookie) || !isTrackingCookieSet(sharedCookieName, callOnceCookie)) {
			const sanitizedObject = removeUndefined(adobeObject);
			// Set Adobe analytics data to global object
			Object.assign(window.nortonAnalytics, sanitizedObject);
			if (!isEmptyObject(sanitizedObject)) {
				this.dtm.push(sanitizedObject);
			}
			// If Adobe s code is loaded, clear interval and push data
			if (window.s) {
				clearInterval(this.dtmInterval);
				this.dtm.forEach((obj) => window.s.trackPageView(obj));
				this.dtm = [];
			} else {
				this.dtmInterval = this.dtmInterval ?? setInterval(() => this._pushToAdobe(), 500);
			}
			// Set tracking cookie
			if (isObject(callOnceCookie)) {
				setTrackingCookie(sharedCookieName, callOnceCookie);
			}
		}
		return this;
	}

	/**
	 * Generic event push
	 * Push to SINGLE DATA LAYER
	 * @param {object} sdlObject
	 * @param {object} callOnceCookie
	 */
	_pushToSingleDataLayer(sdlObject, callOnceCookie) {
		if (!isObject(callOnceCookie) || !isTrackingCookieSet(sharedCookieName, callOnceCookie)) {
			const sanitizedObject = removeUndefined(sdlObject);
			this.sdl.push(sanitizedObject);
		}
		appDebug(`Tracking ${sdlObject.event}`, {
			code: 'APP_FRONTEND_UTIL',
			origin: 'tracking._pushToSingleDataLayer',
			detail: {
				sdlObject
			}
		});
		// Set tracking cookie
		if (isObject(callOnceCookie)) {
			setTrackingCookie(sharedCookieName, callOnceCookie);
		}
	}

	/**
	 * Event userData
	 * @param {object} cart
	 * @returns {object}
	 * @public
	 * @memberof Tracking
	 * @todo Will be part of purchase init call
	 * @deprecated
	 */
	userData(cart) {
		const dataLayerObject = userData(cart);
		return this._pushToDataLayer(dataLayerObject);
	}

	/**
	 * Event account-tooltip-cart-displayed
	 * @param {object} cart
	 * @returns {object}
	 * @public
	 * @memberof Tracking
	 * @deprecated
	 */
	accountTooltipDisplay(cart) {
		const dataLayerObject = accountTooltipDisplay(cart);
		return this._pushToDataLayer(dataLayerObject, {
			name: 'first-account-note-show',
			value: 'false'
		});
	}

	/**
	 * Event addToCart
	 * @param {object} cart
	 * @param {object} impression
	 * @returns {object}
	 * @public
	 * @memberof Tracking
	 * @todo Should be called on modal add
	 */
	addToCart(cart, impression) {
		const dataLayerObject = addToCart(cart, impression);
		return this._pushToDataLayer(dataLayerObject);
	}

	/**
	 * Event buyingForACompany
	 * @param {object} cart
	 * @public
	 * @memberof Tracking
	 */
	buyingForACompany(cart) {
		const dataLayerObject = buyingForACompany(cart);
		const orderObject = {
			buyForCompany: cart.isCompany
		};
		const singleDataLayerObject = singleDataLayer.userUpdateOrder(orderObject, 'buyForCompany');
		// Only if company is true
		if (cart.isCompany) {
			this._pushToDataLayer(dataLayerObject);
		}
		// Push to SingleDataLayer
		this._pushToSingleDataLayer(singleDataLayerObject);
	}

	/**
	 * Event cartOpen
	 * @param {object} cart
	 * @public
	 * @memberof Tracking
	 */
	cartOpen(cart) {
		const dataLayerObject = cartOpen(cart);
		// Single Data Layer
		const singleDataLayerObject = {
			initScreen: singleDataLayer.initScreen(cart),
			screen: singleDataLayer.billingInfo(cart),
			order: singleDataLayer.order(cart)
		};
		// DataLayer push
		this._pushToDataLayer(dataLayerObject, {
			name: 'bi-cartOpen',
			value: cart.order.id
		});
		// SDL Push initialization
		this._pushToSingleDataLayer(singleDataLayerObject.initScreen);
		// SDL Push screen
		this._pushToSingleDataLayer(singleDataLayerObject.screen);
		// SDL Push order
		this._pushToSingleDataLayer(singleDataLayerObject.order);
		// Adobe analytics push
		this._pushToAdobe(dtm.billingInfo(cart));
	}

	/**
	 * Event on first API call
	 * @memberof Tracking
	 */
	cartRequest() {
		const dataLayerObject = cartRequest();
		const singleDataLayerObject = singleDataLayer.systemCartRequest();
		this._pushToDataLayer(dataLayerObject);
		// Push to SingleDataLayer
		this._pushToSingleDataLayer(singleDataLayerObject);
	}

	/**
	 * Event checkout (and cartOpen)
	 * @param {object} cart
	 * @returns {object}
	 * @public
	 * @memberof Tracking
	 * @deprecated Replaced by screen
	 */
	checkout(cart) {
		const dataLayerObject = checkout(cart);
		return this._pushToDataLayer(dataLayerObject);
	}

	/**
	 * Company info screen
	 * @param {object} cart
	 */
	companyInfo(cart) {
		const singleDataLayerObject = {
			screen: singleDataLayer.companyInfo(cart),
			order: singleDataLayer.order(cart)
		};
		this._pushToSingleDataLayer(singleDataLayerObject.screen);
		this._pushToSingleDataLayer(singleDataLayerObject.order);
		// Adobe analytics push
		this._pushToAdobe(dtm.companyInfo(cart));
	}

	/**
	 * Event crossPopClick
	 * @param {object} cart
	 * @param {object} impression
	 * @public
	 * @memberof Tracking
	 */
	crossPopClick(cart, impression) {
		const dataLayerObject = crossPopClick(cart, impression);
		const singleDataLayerObject = singleDataLayer.userAddProducts(cart, [impression]);
		this._pushToDataLayer(dataLayerObject);
		// user.add.products
		this._pushToSingleDataLayer(singleDataLayerObject);
	}

	/**
	 * Sent at the time cross-pop is displayed (impression)
	 * @param {object} cart
	 * @public
	 * @memberof Tracking
	 */
	crossPopImpression(cart) {
		const dataLayerObject = crossPopImpression(cart);
		const singleDataLayerObject = singleDataLayer.crossPop(cart);

		// Regular DataLayer
		this._pushToDataLayer(dataLayerObject);
		// Single DataLayer
		this._pushToSingleDataLayer(singleDataLayerObject);
		// Adobe analytics push
		this._pushToAdobe(dtm.crossPop(cart));
	}

	/**
	 * Track click on review page edit billing details link
	 * @param {object} cart
	 * @public
	 * @memberof Tracking
	 */
	editBilling(cart) {
		const dataLayerObject = editBilling(cart);
		const singleDataLayerObject = singleDataLayer.userClick('user.click.updateBillingInfo');
		this._pushToDataLayer(dataLayerObject, {
			name: 'bi-editBilling',
			value: cart.order.id
		});
		// Push to SingleDataLayer
		this._pushToSingleDataLayer(singleDataLayerObject);
	}

	/**
	 * Track click on review page edit company details link
	 * @param {object} cart
	 * @public
	 * @memberof Tracking
	 */
	editCompany(cart) {
		const dataLayerObject = editCompany(cart);
		const singleDataLayerObject = singleDataLayer.userClick('user.click.updateCompanyInfo');
		this._pushToDataLayer(dataLayerObject, {
			name: 'bi-editCompany',
			value: cart.order.id
		});
		// Push to SingleDataLayer
		this._pushToSingleDataLayer(singleDataLayerObject);
	}

	/**
	 * Track click on review page edit payment details link
	 * @param {object} cart
	 * @public
	 * @memberof Tracking
	 */
	editPayment(cart) {
		const dataLayerObject = editPayment(cart);
		const singleDataLayerObject = singleDataLayer.userClick('user.click.updatePaymentMethod');
		this._pushToDataLayer(dataLayerObject, {
			name: 'bi-editPayment',
			value: cart.order.id
		});
		// Push to SingleDataLayer
		this._pushToSingleDataLayer(singleDataLayerObject);
	}

	/**
	 * Track click on review page edit shipping details link
	 * @param {object} cart
	 * @returns {object}
	 * @public
	 * @memberof Tracking
	 * @deprecated
	 */
	editShipping(cart) {
		const dataLayerObject = editShipping(cart);
		return this._pushToDataLayer(dataLayerObject, {
			name: 'bi-editShipping',
			value: cart.order.id
		});
	}

	/**
	 * Event errorPageStatus
	 * @param {object} cart
	 * @public
	 * @memberof Tracking
	 */
	errorPageStatus(cart) {
		const dataLayerObject = errorPageStatus(cart.error);
		const singleDataLayerObject = singleDataLayer.errorPage(cart);
		this._pushToDataLayer(dataLayerObject);
		// Push to Single Data Layer
		this._pushToSingleDataLayer(singleDataLayerObject);
		// Adobe analytics push
		this._pushToAdobe(dtm.errorPage(cart));
	}

	/**
	 * Event freeTrialEnd
	 * @param {object} cart
	 * @param {string} selectedPaymentMethod
	 * @returns {object}
	 * @public
	 * @memberof Tracking
	 * @deprecated
	 */
	freeTrialEnd(cart, selectedPaymentMethod) {
		const dataLayerObject = freeTrialEnd(cart, selectedPaymentMethod);
		return this._pushToDataLayer(dataLayerObject);
	}

	/**
	 * Event jsError
	 * @param {object} error
	 * @public
	 * @memberof Tracking
	 */
	jsError(error) {
		const dataLayerObject = jsError(error);
		const errorObject = {
			code: error.app.code,
			message: `${error.lineNumber || 0}:${error.columnNumber || 0}:${error.fileName || 0}`,
			description: `${error.app.code || 'APP'} | ${error.app.origin || 'Undefined'} | ${error.name || 'Error'}: ${error.message || 'Undefined'}`
		};
		const singleDataLayerObject = singleDataLayer.systemError(errorObject, 'jsError');
		this._pushToDataLayer(dataLayerObject);
		// Push to SingleDataLayer object
		this._pushToSingleDataLayer(singleDataLayerObject);
	}

	/**
	 * Event jsError (used to track authenticated accounts mismatch)
	 * @param {'AccountMismatch'|'AccountMismatchGuest'|'AccountMismatchError'|'AccountMismatchNull'|'AccountMismatchEmail'} mismatchCode Code of the mismatch to better understand
	 * @param {string} sessionId Checkout session ID in which this error happened
	 * @public
	 * @memberof Tracking
	 */
	accountMismatch(mismatchCode, sessionId) {
		const dataLayerObject = {
			event: 'jsError',
			jsErrorInfo: {
				line: '0:0:0',
				message: `APP | openCartMiddleware | ${mismatchCode}: ${sessionId}`
			}
		};
		const singleDataLayerObject = {
			system: {
				error: {
					code: 'APP',
					message: dataLayerObject.jsErrorInfo.line,
					type: dataLayerObject.event,
					description: dataLayerObject.jsErrorInfo.message
				}
			},
			event: 'system.error'
		};
		this._pushToDataLayer(dataLayerObject);
		this._pushToSingleDataLayer(singleDataLayerObject);
	}

	/**
	 * Event emailOpen (Thank you page)
	 * @param {object} cart
	 * @param {string} domain
	 * @returns {object}
	 * @public
	 * @memberof Tracking
	 * @todo Should be resolved with Alex
	 */
	emailOpen(cart, domain) {
		const dataLayerObject = emailOpen(cart, domain);
		return this._pushToDataLayer(dataLayerObject);
	}

	/**
	 * Event emailOpenClick (Thank you page)
	 * @param {object} cart
	 * @param {string} domain
	 * @returns {object}
	 * @public
	 * @memberof Tracking
	 * @todo Should be resolved with Alex
	 */
	emailOpenClick(cart, domain) {
		const dataLayerObject = emailOpenClick(cart, domain);
		return this._pushToDataLayer(dataLayerObject);
	}

	/**
	 * Event paymentAuthorisationFailed
	 * @param {object} cart
	 * @param {object} error
	 * @public
	 * @memberof Tracking
	 */
	paymentAuthorisationFailed(cart, error) {
		const dataLayerObject = paymentAuthorisationFailed(cart, error);
		const errorObject = {
			status: error.errorCode,
			message: error.errorDescription,
			description: error.paymentMethod || 'Undefined'
		};
		const singleDataLayerObject = {
			jsError: singleDataLayer.systemError(errorObject, 'jsError'),
			authorizationError: singleDataLayer.systemError(errorObject, 'paymentAuthorisationFailed'),
			errorPage: singleDataLayer.errorPage(cart, errorObject)
		};

		this._pushToDataLayer(dataLayerObject);
		// Push to sdl
		this._pushToSingleDataLayer(singleDataLayerObject.jsError);
		this._pushToSingleDataLayer(singleDataLayerObject.authorizationError);
		this._pushToSingleDataLayer(singleDataLayerObject.errorPage);
		// Push to adobe
		this._pushToAdobe(dtm.errorPage(cart, 'payment failure'));
	}

	/**
	 * Event paymentSelected
	 * @param {object} cart
	 * @param {object} selectedPaymentMethod
	 * @public
	 * @memberof Tracking
	 */
	paymentSelected(cart, selectedPaymentMethod) {
		const dataLayerObject = paymentSelected(cart, selectedPaymentMethod);
		const orderObject = {
			paymentMethod: selectedPaymentMethod,
			paymentProvider: '-'
		};
		const singleDataLayerObject = singleDataLayer.userUpdateOrder(orderObject, 'confirmPaymentMethod');
		this._pushToDataLayer(dataLayerObject);
		// Push to Single Data Layer
		this._pushToSingleDataLayer(singleDataLayerObject);
	}

	/**
	 * Track click on PPEC button
	 * @param {object} cart
	 * @returns {object}
	 * @public
	 * @memberof Tracking
	 * @deprecated
	 */
	payPalButtonClick(cart) {
		const dataLayerObject = payPalButtonClick(cart);
		return this._pushToDataLayer(dataLayerObject);
	}

	/**
	 * Event paymentSourceCreation
	 * @param {object} cart
	 * @param {object} options
	 * @param {string} [options.description] Description or error info
	 * @param {boolean} [options.hasError] Determine if error
	 * @public
	 * @memberof Tracking
	 */
	paymentSourceCreation(cart, { description = '', hasError = false } = {}) {
		const paymentObjectType = hasError ? 'paymentObjectError' : 'paymentObjectCreated';
		// Push SingleDataLayer object
		this._pushToSingleDataLayer(singleDataLayer.systemServiceInfo(cart, paymentObjectType, description));
	}

	/**
	 * Event payment (visit of Payment Details Page)
	 * @note Currently used in Brazil flow where payment details are on a separate page
	 * @param {object} cart
	 * @public
	 * @memberof Tracking
	 */
	paymentDetails(cart) {
		this._pushToAdobe(dtm.paymentDetails(cart));
	}

	/**
	 * Event productActivation
	 * @param {object} cart
	 * @param {object} product
	 * @public
	 * @todo Multiple events due to product activation
	 * @memberof Tracking
	 */
	productActivation(cart, product) {
		const dataLayerObject = productActivation(cart, product);
		const singleDataLayerObject = singleDataLayer.systemActivation(cart, product);
		this._pushToDataLayer(dataLayerObject);
		// Push SingleDataLayer object
		this._pushToSingleDataLayer(singleDataLayerObject);
	}

	/**
	 * Event purchase
	 * @param {object} cart
	 * @public
	 * @memberof Tracking
	 */
	purchase(cart) {
		const dataLayerObject = purchase(cart);
		const singleDataLayerObject = {
			initScreen: singleDataLayer.initScreen(cart),
			thankyou: singleDataLayer.thankyouPage(cart),
			systemOrder: singleDataLayer.systemOrder(cart),
			order: singleDataLayer.order(cart)
		};

		this._pushToDataLayer(dataLayerObject, {
			name: 'bi-purchase',
			value: cart.order.id
		});
		// Push screen init
		this._pushToSingleDataLayer(singleDataLayerObject.initScreen);
		// Push Thank you page screen
		this._pushToSingleDataLayer(singleDataLayerObject.thankyou);
		// Push order
		this._pushToSingleDataLayer(singleDataLayerObject.order);
		// Push System order only once
		this._pushToSingleDataLayer(singleDataLayerObject.systemOrder, {
			name: 'bi-purchase-sdl',
			value: cart.order.id
		});
		this._pushToAdobe(dtm.thankYouPage(cart), {
			name: 'bi-purchase-dtm',
			value: cart.order.id
		});
	}

	/**
	 * Event removeFromCart
	 * @param {object} cart
	 * @param {object} product
	 * @public
	 * @memberof Tracking
	 */
	removeFromCart(cart, product) {
		const dataLayerObject = removeFromCart(cart, product);
		const singleDataLayerObject = singleDataLayer.userRemoveProducts(cart, product);
		const emptyOrderObject = {
			order: {
				products: null
			}
		};
		this._pushToDataLayer(dataLayerObject);
		// Reset order products array
		this._pushToSingleDataLayer(emptyOrderObject);
		// Push to SingleDataLayer
		this._pushToSingleDataLayer(singleDataLayerObject);
	}

	/**
	 * Event review (Unique visit of Review Page)
	 * @param {object} cart
	 * @public
	 * @memberof Tracking
	 */
	review(cart) {
		const dataLayerObject = review(cart);
		const singleDataLayerObject = {
			review: singleDataLayer.reviewPage(cart),
			initScreen: singleDataLayer.initScreen(cart),
			order: singleDataLayer.order(cart)
		};
		this._pushToDataLayer(dataLayerObject, {
			name: 'bi-review',
			value: cart.order.id
		});
		// Push screen init
		this._pushToSingleDataLayer(singleDataLayerObject.initScreen);
		// Push to single data layer
		this._pushToSingleDataLayer(singleDataLayerObject.review);
		// Push order only once
		this._pushToSingleDataLayer(singleDataLayerObject.order);
		// Adobe analytics push
		this._pushToAdobe(dtm.reviewPage(cart));
	}

	/**
	 * Event restoreCart
	 * @returns {object}
	 * @public
	 * @memberof Tracking
	 */
	restoreCart() {
		const dataLayerObject = restoreCart();
		return this._pushToDataLayer(dataLayerObject);
	}

	/**
	 * Event submitOrder
	 * @param {object} cart
	 * @public
	 * @memberof Tracking
	 */
	submitOrder(cart) {
		const dataLayerObject = submitOrder(cart);
		const singleDataLayerObject = singleDataLayer.userClick('user.submit.order');
		this._pushToDataLayer(dataLayerObject);
		this._pushToSingleDataLayer(singleDataLayerObject);
	}

	/**
	 * Track click on support button in a sidebar
	 * @param {object} cart
	 * @returns {object}
	 * @public
	 * @memberof Tracking
	 * @deprecated
	 */
	supportButton(cart) {
		const dataLayerObject = supportButton(cart);
		return this._pushToDataLayer(dataLayerObject, {
			name: `bi-supportButtonStep${cart.page.number}`,
			value: `${cart.order.id}_${cart.page.number}`
		});
	}

	/**
	 * Event emailTypo (Email suggestion)
	 * @param {object} cart
	 * @param {string} originalDomain
	 * @param {string} [suggestedDomain]
	 * @public
	 * @memberof Tracking
	 */
	emailTypo(cart, originalDomain, suggestedDomain) {
		const dataLayerObject = emailTypo(cart, originalDomain, suggestedDomain);
		const singleDataLayerObject = singleDataLayer.systemHint(originalDomain, suggestedDomain, 'billingInfo.emailAddress', 'input.suggestion');
		this._pushToDataLayer(dataLayerObject, {
			name: 'bi-email-typo',
			value: cart.order.id
		});
		// Push to SingleDataLayer
		this._pushToSingleDataLayer(singleDataLayerObject, {
			name: 'bi-email-typo-sdl',
			value: cart.order.id
		});
	}

	/**
	 * Event emailTypoClick (Email suggestion)
	 * @param {object} cart
	 * @param {string} originalDomain
	 * @param {string} suggestedDomain
	 * @public
	 * @memberof Tracking
	 */
	emailTypoClick(cart, originalDomain, suggestedDomain) {
		const dataLayerObject = emailTypoClick(cart, originalDomain, suggestedDomain);
		const singleDataLayerObject = singleDataLayer.userHint(originalDomain, suggestedDomain, 'billingInfo.emailAddress', 'input.suggestion');
		this._pushToDataLayer(dataLayerObject, {
			name: 'bi-email-typo-click',
			value: cart.order.id
		});
		// user accepted hint
		this._pushToSingleDataLayer(singleDataLayerObject, {
			name: 'bi-email-typo-click-sdl',
			value: cart.order.id
		});
	}

	/**
	 * Event virtualPageview (Sends content group to GTM)
	 * @param {object} cart
	 * @returns {object}
	 * @public
	 * @memberof Tracking
	 * @deprecated
	 */
	virtualPageview(cart) {
		const dataLayerObject = virtualPageview(cart);
		return this._pushToDataLayer(dataLayerObject);
	}

	/**
	 * Invoice page tracking
	 * @param {object} cart
	 * @todo will be resolved later
	 */
	invoicePage(cart) {
		// Adobe analytics push
		this._pushToAdobe(dtm.invoicePage(cart));
	}

}
